/**
 * GM Tool — 开发者调试面板（v1.4.0）
 *
 * 设计原则：
 *   - 全局弹窗覆盖层，不依赖路由，任何页面可唤起
 *   - 入口隐藏：右下角 8px 触发点三连击 / Ctrl+Shift+G
 *   - 纯前端操作，直接读写 localStorage，零网络依赖
 *   - Ardot 战术美学：0 圆角、1-2px 描边、monospace
 *
 * Tab 模块：
 *   1. LLM  — API Key & 配额管理
 *   2. 诊断 — localStorage 全景 & browser.log
 *   3. 数据 — 导入/导出/重置
 *   4. 导航 — 快速跳转
 *   5. Dev  — 本地调试（v1.4.0 新增）：假登录 / 导入生产 token / 清理残留
 */

// ===== 类型定义 =====

type GmTab = 'llm' | 'diag' | 'data' | 'nav' | 'dev';

interface GmState {
  open: boolean;
  activeTab: GmTab;
  localStorageKeys: Array<{ key: string; value: string; size: number }>;
}

// ===== 常量 =====

const TRIGGER_SIZE = 24;
const TRIPLE_CLICK_WINDOW = 800; // ms
const OVERLAY_Z = 99999;

const TAB_LABELS: Record<GmTab, string> = {
  llm: 'LLM',
  diag: '诊断',
  data: '数据',
  nav: '导航',
  dev: 'Dev',
};

// ===== 全局单例 =====

const state: GmState = {
  open: false,
  activeTab: 'dev',
  localStorageKeys: [],
};

let overlayEl: HTMLElement | null = null;
let triggerEl: HTMLElement | null = null;

// ===== 三连击检测 =====

let clickCount = 0;
let clickTimer = 0;
let badgeEl: HTMLSpanElement | null = null;

function showClickBadge(count: number): void {
  if (!triggerEl) return;
  if (!badgeEl) {
    badgeEl = document.createElement('span');
    badgeEl.style.cssText = `
      position: absolute;
      top: -18px;
      right: 2px;
      font-family: monospace;
      font-size: 10px;
      color: #F8B700;
      pointer-events: none;
      opacity: 0;
      transition: opacity 0.1s;
    `;
    triggerEl.appendChild(badgeEl);
  }
  badgeEl.textContent = `${count}/3`;
  badgeEl.style.opacity = '1';
  clearTimeout(Number(badgeEl.dataset.hideTimer));
  badgeEl.dataset.hideTimer = String(window.setTimeout(() => {
    if (badgeEl) badgeEl.style.opacity = '0';
  }, 600));
}

function onTriggerClick(): void {
  clickCount++;
  showClickBadge(clickCount);
  if (clickCount === 1) {
    clickTimer = window.setTimeout(() => {
      clickCount = 0;
      if (badgeEl) badgeEl.style.opacity = '0';
    }, TRIPLE_CLICK_WINDOW);
  }
  if (clickCount >= 3) {
    clearTimeout(clickTimer);
    clickCount = 0;
    if (badgeEl) badgeEl.style.opacity = '0';
    try {
      toggle();
    } catch (e) {
      console.error('[GM] toggle 异常:', e);
    }
  }
}

// ===== 键盘快捷键 =====

function onKeydown(e: KeyboardEvent): void {
  if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'G') {
    e.preventDefault();
    toggle();
  }
  if (e.key === 'Escape' && state.open) {
    close();
  }
}

// ===== 注入触发器 =====

export function setupGmTrigger(): void {
  // 右下角隐藏触发点（极淡背景，hover 时变亮提示存在）
  triggerEl = document.createElement('div');
  triggerEl.id = 'gm-trigger';
  triggerEl.title = '三连击打开 GM 工具';
  triggerEl.style.cssText = `
    position: fixed;
    bottom: 0;
    right: 0;
    width: ${TRIGGER_SIZE}px;
    height: ${TRIGGER_SIZE}px;
    z-index: ${OVERLAY_Z - 1};
    cursor: pointer;
    pointer-events: auto;
    background: rgba(248, 183, 0, 0.06);
    border-left: 1px solid rgba(248, 183, 0, 0.08);
    border-top: 1px solid rgba(248, 183, 0, 0.08);
    transition: background 0.2s, border-color 0.2s;
  `;
  triggerEl.addEventListener('click', onTriggerClick);
  triggerEl.addEventListener('mouseenter', () => {
    if (triggerEl) {
      triggerEl.style.background = 'rgba(248, 183, 0, 0.18)';
      triggerEl.style.borderColor = 'rgba(248, 183, 0, 0.3)';
    }
  });
  triggerEl.addEventListener('mouseleave', () => {
    if (triggerEl) {
      triggerEl.style.background = 'rgba(248, 183, 0, 0.06)';
      triggerEl.style.borderColor = 'rgba(248, 183, 0, 0.08)';
    }
  });
  document.body.appendChild(triggerEl);

  // 键盘快捷键
  document.addEventListener('keydown', onKeydown);

  console.log('[GM] 触发器已就绪：右下角三连击 或 Ctrl+Shift+G');
}

// ===== 开关 =====

export function toggle(): void {
  if (state.open) {
    close();
  } else {
    open();
  }
}

export function open(): void {
  if (state.open) return;
  state.open = true;

  try {
    refreshData();

    overlayEl = document.createElement('div');
    overlayEl.id = 'gm-overlay';
    overlayEl.className = 'gm-overlay';
    overlayEl.addEventListener('click', (e) => {
      if (e.target === overlayEl) close();
    });
    document.body.appendChild(overlayEl);

    renderPanel();

    document.body.style.overflow = 'hidden';
  } catch (e) {
    console.error('[GM] open() 异常:', e);
    state.open = false;
    if (overlayEl?.parentNode) {
      overlayEl.parentNode.removeChild(overlayEl);
    }
    overlayEl = null;
  }
}

export function close(): void {
  if (!state.open) return;
  state.open = false;

  if (overlayEl && overlayEl.parentNode) {
    overlayEl.parentNode.removeChild(overlayEl);
  }
  overlayEl = null;

  document.body.style.overflow = '';
}

function refreshData(): void {
  state.localStorageKeys = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith('battlecode-')) {
      const value = localStorage.getItem(key) ?? '';
      state.localStorageKeys.push({
        key,
        value: value.length > 200 ? value.slice(0, 200) + '...' : value,
        size: new Blob([value]).size,
      });
    }
  }
}

// ===== 面板渲染 =====

function renderPanel(): void {
  if (!overlayEl) return;

  overlayEl.innerHTML = `
    <div class="gm-panel">
      <div class="gm-header">
        <span class="gm-header-title">GM TOOLS</span>
        <button class="gm-btn gm-btn-close" id="gm-btn-close" title="关闭 (ESC)">X</button>
      </div>
      <div class="gm-body">
        <nav class="gm-tabs" id="gm-tabs">
          ${(Object.keys(TAB_LABELS) as GmTab[]).map((tab) => `
            <button class="gm-tab ${tab === state.activeTab ? 'gm-tab--active' : ''}"
                    data-tab="${tab}">
              ${TAB_LABELS[tab]}
            </button>
          `).join('')}
        </nav>
        <div class="gm-content" id="gm-content"></div>
      </div>
      <div class="gm-footer">
        <span>BattleCode GM v1.4</span>
        <span class="gm-footer-dot">●</span>
        <span>ESC 关闭</span>
        <span class="gm-footer-dot">●</span>
        <span>Ctrl+Shift+G 切换</span>
      </div>
    </div>
  `;

  // 事件绑定
  overlayEl.querySelector('#gm-btn-close')?.addEventListener('click', close);
  overlayEl.querySelector('#gm-tabs')?.addEventListener('click', (e) => {
    const btn = (e.target as HTMLElement).closest('.gm-tab') as HTMLElement | null;
    if (!btn) return;
    const tab = btn.dataset.tab as GmTab;
    if (tab) switchTab(tab);
  });

  renderTabContent();
}

function switchTab(tab: GmTab): void {
  state.activeTab = tab;
  // 更新 tab 高亮
  document.querySelectorAll('.gm-tab').forEach((el) => {
    el.classList.toggle('gm-tab--active', (el as HTMLElement).dataset.tab === tab);
  });
  renderTabContent();
}

function renderTabContent(): void {
  const content = document.getElementById('gm-content');
  if (!content) return;

  switch (state.activeTab) {
    case 'llm':
      renderLlmTab(content);
      break;
    case 'diag':
      renderDiagTab(content);
      break;
    case 'data':
      renderDataTab(content);
      break;
    case 'nav':
      renderNavTab(content);
      break;
    case 'dev':
      renderDevTab(content);
      break;
  }
}

// ===== Tab 1: LLM =====

function renderLlmTab(container: HTMLElement): void {
  const apiKey = localStorage.getItem('battlecode-llm-api-key');
  const demoKey = 'sk-0e72404214204a839e095e9cc7f8655a';
  const isUsingDemo = !apiKey || apiKey === demoKey;
  const maskedKey = apiKey
    ? (apiKey.length > 12 ? apiKey.slice(0, 8) + '...' + apiKey.slice(-4) : '***')
    : '未设置';

  const dailyCount = parseInt(localStorage.getItem('battlecode-llm-daily-count') ?? '0', 10);
  const dailyDate = localStorage.getItem('battlecode-llm-daily-date');
  const today = new Date().toISOString().slice(0, 10);
  const isTodayCount = dailyDate === today;

  container.innerHTML = `
    <div class="gm-section">
      <div class="gm-section-header">API Key 状态</div>
      <table class="gm-table">
        <tr><td class="gm-label">当前 Key</td><td class="gm-mono">${maskedKey}</td></tr>
        <tr><td class="gm-label">来源</td><td>${isUsingDemo ? 'Demo 预置 Key' : '用户自定义 Key'}</td></tr>
        <tr><td class="gm-label">今日调用</td><td>${isTodayCount ? dailyCount : 0} / 100</td></tr>
      </table>

      <div class="gm-quick-actions" style="margin-top:12px">
        <button class="gm-btn gm-btn-sm" id="gm-llm-reset-quota">重置今日配额</button>
        <button class="gm-btn gm-btn-sm" id="gm-llm-clear-key">清除自定义 Key（恢复 Demo Key）</button>
        <button class="gm-btn gm-btn-sm" id="gm-llm-set-demo">强制使用 Demo Key</button>
      </div>

      <div class="gm-section-header" style="margin-top:20px">LLM 引擎信息</div>
      <table class="gm-table">
        <tr><td class="gm-label">API 端点</td><td class="gm-mono">https://api.deepseek.com/v1/chat/completions</td></tr>
        <tr><td class="gm-label">模型</td><td class="gm-mono">deepseek-coder</td></tr>
        <tr><td class="gm-label">每日限额</td><td>100 次</td></tr>
      </table>
    </div>
  `;

  container.querySelector('#gm-llm-reset-quota')?.addEventListener('click', () => {
    localStorage.setItem('battlecode-llm-daily-count', '0');
    localStorage.setItem('battlecode-llm-daily-date', today);
    renderLlmTab(container);
  });
  container.querySelector('#gm-llm-clear-key')?.addEventListener('click', () => {
    localStorage.removeItem('battlecode-llm-api-key');
    renderLlmTab(container);
  });
  container.querySelector('#gm-llm-set-demo')?.addEventListener('click', () => {
    localStorage.setItem('battlecode-llm-api-key', demoKey);
    renderLlmTab(container);
  });
}

// ===== Tab 2: 诊断 =====

function renderDiagTab(container: HTMLElement): void {
  const keys = state.localStorageKeys;
  const totalSize = keys.reduce((sum, k) => sum + k.size, 0);

  container.innerHTML = `
    <div class="gm-section">
      <div class="gm-section-header">
        localStorage 全景
        <span class="gm-stat">${keys.length} 键</span>
        <span class="gm-stat">${(totalSize / 1024).toFixed(1)} KB</span>
      </div>
      <table class="gm-table">
        <thead>
          <tr>
            <th>Key</th>
            <th>大小</th>
            <th>预览</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          ${keys.map((k) => `
            <tr>
              <td class="gm-mono gm-key-cell">${k.key}</td>
              <td class="gm-mono">${formatBytes(k.size)}</td>
              <td class="gm-mono gm-text-dim gm-preview-cell">${escapeHtml(k.value.slice(0, 80))}</td>
              <td>
                <button class="gm-btn gm-btn-xs" data-key="${k.key}" data-action="view">查看</button>
                <button class="gm-btn gm-btn-xs" data-key="${k.key}" data-action="copy">复制</button>
                <button class="gm-btn gm-btn-xs gm-btn-danger" data-key="${k.key}" data-action="delete">删除</button>
              </td>
            </tr>
          `).join('')}
          ${keys.length === 0 ? '<tr><td colspan="4" class="gm-empty">无数据</td></tr>' : ''}
        </tbody>
      </table>

      <div class="gm-quick-actions" style="margin-top:12px">
        <button class="gm-btn gm-btn-sm" id="gm-diag-refresh">刷新</button>
        <button class="gm-btn gm-btn-sm gm-btn-danger" id="gm-diag-clear-all">清除全部数据</button>
      </div>

      <div class="gm-section-header" style="margin-top:20px">系统信息</div>
      <table class="gm-table">
        <tr><td class="gm-label">User Agent</td><td class="gm-mono gm-text-dim" style="font-size:10px;max-width:400px;word-break:break-all">${navigator.userAgent}</td></tr>
        <tr><td class="gm-label">localStorage 剩余</td><td class="gm-mono">估算中...</td></tr>
        <tr><td class="gm-label">Pyodide</td><td class="gm-mono">浏览器内 WASM</td></tr>
      </table>
    </div>
  `;

  // 按钮事件
  container.querySelectorAll('[data-action="view"]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const key = (btn as HTMLElement).dataset.key!;
      const value = localStorage.getItem(key) ?? '(空)';
      try {
        const parsed = JSON.parse(value);
        alert(`${key}:\n${JSON.stringify(parsed, null, 2)}`);
      } catch {
        alert(`${key}:\n${value}`);
      }
    });
  });

  container.querySelectorAll('[data-action="copy"]').forEach((btn) => {
    btn.addEventListener('click', async () => {
      const key = (btn as HTMLElement).dataset.key!;
      const value = localStorage.getItem(key) ?? '';
      await navigator.clipboard.writeText(value);
    });
  });

  container.querySelectorAll('[data-action="delete"]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const key = (btn as HTMLElement).dataset.key!;
      if (confirm(`确定删除 "${key}"？`)) {
        localStorage.removeItem(key);
        refreshData();
        renderDiagTab(container);
      }
    });
  });

  container.querySelector('#gm-diag-refresh')?.addEventListener('click', () => {
    refreshData();
    renderDiagTab(container);
  });

  container.querySelector('#gm-diag-clear-all')?.addEventListener('click', () => {
    if (confirm('确定清除所有 battlecode-* 数据？此操作不可撤销！')) {
      const toRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('battlecode-')) toRemove.push(key);
      }
      toRemove.forEach((k) => localStorage.removeItem(k));
      refreshData();
      renderDiagTab(container);
    }
  });
}

// ===== Tab 3: 数据管理 =====

function renderDataTab(container: HTMLElement): void {
  container.innerHTML = `
    <div class="gm-section">
      <div class="gm-section-header">数据导入/导出</div>

      <div class="gm-data-block">
        <div class="gm-data-block-title">全部 localStorage 备份</div>
        <textarea class="gm-textarea" id="gm-data-all-export" readonly rows="8"
                  placeholder="点击导出按钮..."></textarea>
        <div class="gm-quick-actions">
          <button class="gm-btn gm-btn-sm" id="gm-data-all-export-btn">导出全部</button>
          <button class="gm-btn gm-btn-sm" id="gm-data-all-copy-btn">复制</button>
        </div>
      </div>

      <div class="gm-section-header" style="margin-top:20px">危险操作</div>
      <div class="gm-quick-actions">
        <button class="gm-btn gm-btn-sm gm-btn-danger" id="gm-data-reset-all">清除全部数据</button>
      </div>
    </div>
  `;

  // 导出全部
  container.querySelector('#gm-data-all-export-btn')?.addEventListener('click', () => {
    const allData: Record<string, unknown> = {};
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('battlecode-')) {
        try {
          allData[key] = JSON.parse(localStorage.getItem(key) ?? '');
        } catch {
          allData[key] = localStorage.getItem(key);
        }
      }
    }
    const textarea = document.getElementById('gm-data-all-export') as HTMLTextAreaElement;
    if (textarea) textarea.value = JSON.stringify(allData, null, 2);
  });

  // 复制全部
  container.querySelector('#gm-data-all-copy-btn')?.addEventListener('click', async () => {
    const textarea = document.getElementById('gm-data-all-export') as HTMLTextAreaElement;
    const text = textarea?.value;
    if (text) await navigator.clipboard.writeText(text);
  });

  // 清除全部
  container.querySelector('#gm-data-reset-all')?.addEventListener('click', () => {
    if (confirm('确定清除所有 battlecode-* 数据？此操作不可撤销！')) {
      for (let i = localStorage.length - 1; i >= 0; i--) {
        const key = localStorage.key(i);
        if (key && key.startsWith('battlecode-')) {
          localStorage.removeItem(key);
        }
      }
      refreshData();
    }
  });
}

// ===== Tab 4: 导航 =====

function renderNavTab(container: HTMLElement): void {
  const screens = [
    { label: '首页 — 赛博指挥终端', hash: '#/' },
    { label: '关卡列表', hash: '#/levels' },
    { label: '砺锋台', hash: '#/sharpen' },
    { label: '铸剑阁', hash: '#/forge' },
    { label: '进化竞技场', hash: '#/arena' },
    { label: '地图编辑器', hash: '#/editor-map/new' },
    { label: '设置', hash: '#/settings' },
    { label: '展示验证器', hash: '#/showcase-validator' },
  ];

  container.innerHTML = `
    <div class="gm-section">
      <div class="gm-section-header">快速跳转</div>
      <div class="gm-nav-grid">
        ${screens.map((s) => `
          <button class="gm-btn gm-btn-nav" data-hash="${s.hash}">
            ${s.label}
          </button>
        `).join('')}
      </div>
    </div>
  `;

  container.querySelectorAll('.gm-btn-nav').forEach((btn) => {
    btn.addEventListener('click', () => {
      const hash = (btn as HTMLElement).dataset.hash!;
      close();
      window.location.hash = hash;
    });
  });
}

// ===== 工具函数 =====

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  return `${(bytes / 1024).toFixed(1)} KB`;
}

function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

// ===== Tab 5: Dev（本地调试能力） =====
/**
 * 仅用于本地 / 私有部署调试。
 * 生产环境（battlecode.vb101.cn）也会渲染此 tab，但按钮注入的 fake user 无法通过
 * 服务端 JWT 校验（仅 isLoggedIn() 客户端判断会通过），仅供 UI 流程验证。
 */

const FAKE_USER_OPENID = "dev:fakeuser";
const FAKE_USER_NAME = "Dev User";
const FAKE_USER_PROVIDER = "dev";
const FAKE_TOKEN_PREFIX = "dev.fake.";

interface JwtClaims {
  openid?: string;
  provider?: string;
  name?: string;
  avatar?: string;
  exp?: number;
}

function decodeJwtClaims(token: string): JwtClaims | null {
  const parts = token.split(".");
  if (parts.length < 2) return null;
  try {
    const payload = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = payload.padEnd(Math.ceil(payload.length / 4) * 4, "=");
    return JSON.parse(atob(padded)) as JwtClaims;
  } catch {
    return null;
  }
}

function buildFakeToken(openid: string, name: string): string {
  // 本地 fake token，结构兼容 JWT（header.payload.signature），供 isLoggedIn() 通过
  const header = btoa(JSON.stringify({ alg: "none", typ: "JWT" })).replace(/=+$/, "");
  const payload = btoa(
    JSON.stringify({ openid, name, provider: FAKE_USER_PROVIDER, iat: Math.floor(Date.now() / 1000), exp: Math.floor(Date.now() / 1000) + 86400 * 365 })
  ).replace(/=+$/, "");
  return `${FAKE_TOKEN_PREFIX}${header}.${payload}.fakesig`;
}

function readAuthStatus(): {
  loggedIn: boolean;
  openid: string | null;
  name: string | null;
  provider: string | null;
  hasToken: boolean;
  tokenExp: number | null;
  tokenExpired: boolean | null;
  tokenIsFake: boolean;
} {
  const rawUser = localStorage.getItem("battlecode-current-user");
  const token = localStorage.getItem("battlecode-auth-token");
  const rawProfile = localStorage.getItem("battlecode-user-profile");

  let openid: string | null = null;
  let name: string | null = null;
  try {
    if (rawUser) {
      const u = JSON.parse(rawUser) as { openid?: string; name?: string };
      openid = u.openid ?? null;
      name = u.name ?? null;
    }
  } catch { /* ignore */ }

  let provider: string | null = null;
  try {
    if (rawProfile) {
      const p = JSON.parse(rawProfile) as { provider?: string };
      provider = p.provider ?? null;
    }
  } catch { /* ignore */ }

  const tokenIsFake = !!token && token.startsWith(FAKE_TOKEN_PREFIX);
  const claims = token ? decodeJwtClaims(token) : null;
  const tokenExp = claims?.exp ?? null;
  const tokenExpired = tokenExp == null ? null : tokenExp * 1000 <= Date.now();

  return {
    loggedIn: !!openid && !!token,
    openid,
    name,
    provider,
    hasToken: !!token,
    tokenExp,
    tokenExpired,
    tokenIsFake,
  };
}

function renderDevTab(container: HTMLElement): void {
  const status = readAuthStatus();
  const hostname = window.location.hostname;
  const isLocal = hostname === "localhost" || hostname === "127.0.0.1" || hostname === "" || hostname.endsWith(".local");
  const pendingAuthPath = localStorage.getItem("battlecode-pending-auth-path");
  const pendingForge = localStorage.getItem("battlecode-pending-forge");
  const expStr = status.tokenExp
    ? `${new Date(status.tokenExp * 1000).toLocaleString()} (${status.tokenExpired ? "已过期" : "有效"})`
    : "—";

  container.innerHTML = `
    <div class="gm-section">
      <div class="gm-section-header">
        调试能力
        <span class="gm-stat">${isLocal ? "本地" : "远程"} · ${escapeHtml(hostname)}</span>
      </div>

      ${!isLocal ? `
        <div class="gm-warn">
          ⚠️ 当前非本地环境。这些按钮会让 isLoggedIn() 返回 true，但 fake token 无法通过服务端校验，
          仅供 UI 流程验证（如关卡加载过场、登录拦截等）。生产环境请走正常登录。
        </div>
      ` : `
        <div class="gm-hint">
          本地 dev server 无后端（Vite only），OAuth 登录会跳到 /api/auth/* 404。
          用以下按钮直接注入登录态，无需后端。
        </div>
      `}

      <div class="gm-section-header" style="margin-top:16px">当前登录态</div>
      <table class="gm-table">
        <tr><td class="gm-label">isLoggedIn()</td><td class="gm-mono">${status.loggedIn ? "✅ true" : "❌ false"}</td></tr>
        <tr><td class="gm-label">openid</td><td class="gm-mono gm-text-dim">${status.openid ?? "—"}</td></tr>
        <tr><td class="gm-label">name</td><td class="gm-mono">${status.name ?? "—"}</td></tr>
        <tr><td class="gm-label">provider</td><td class="gm-mono">${status.provider ?? "—"}</td></tr>
        <tr><td class="gm-label">token</td><td class="gm-mono">${status.hasToken ? (status.tokenIsFake ? "🎭 fake" : "✅ present") : "—"}</td></tr>
        <tr><td class="gm-label">token exp</td><td class="gm-mono">${expStr}</td></tr>
      </table>

      <div class="gm-section-header" style="margin-top:16px">本地登录</div>
      <div class="gm-quick-actions">
        <button class="gm-btn gm-btn-sm" id="gm-dev-fake-login" title="无需后端，直接注入 fake 登录态">
          🎭 一键假登录
        </button>
        <button class="gm-btn gm-btn-sm gm-btn-danger" id="gm-dev-logout" title="清除 token + current-user + user-profile">
          登出
        </button>
      </div>

      <div class="gm-section-header" style="margin-top:16px">从生产 token 导入</div>
      <div class="gm-data-block">
        <div class="gm-data-block-title">粘贴 prod JWT（eyJ... 开头）</div>
        <textarea class="gm-textarea" id="gm-dev-token-input" rows="3"
                  placeholder="eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9..."></textarea>
        <div class="gm-quick-actions">
          <button class="gm-btn gm-btn-sm" id="gm-dev-import-token">解码并注入</button>
          <button class="gm-btn gm-btn-sm" id="gm-dev-decode-only">只解码查看</button>
        </div>
        <div id="gm-dev-token-preview" class="gm-text-dim" style="font-size:11px;margin-top:6px;font-family:monospace"></div>
      </div>

      <div class="gm-section-header" style="margin-top:16px">清理残留</div>
      <table class="gm-table">
        <tr>
          <td class="gm-label">pending-auth-path</td>
          <td class="gm-mono gm-text-dim">${pendingAuthPath ?? "—"}</td>
          <td>${pendingAuthPath ? '<button class="gm-btn gm-btn-xs" id="gm-dev-clear-pending-auth">清</button>' : ""}</td>
        </tr>
        <tr>
          <td class="gm-label">pending-forge</td>
          <td class="gm-mono gm-text-dim">${pendingForge ?? "—"}</td>
          <td>${pendingForge ? '<button class="gm-btn gm-btn-xs" id="gm-dev-clear-pending-forge">清</button>' : ""}</td>
        </tr>
      </table>

      <div class="gm-section-header" style="margin-top:16px">铸剑阁进度控制</div>
      <div class="gm-quick-actions">
        <button class="gm-btn gm-btn-sm" id="gm-forge-unlock-all">🔓 一键全解锁</button>
      </div>
      <table class="gm-table" style="margin-top:8px">
        ${[1,2,3,4,5].map(n => `
        <tr>
          <td class="gm-label">章${n}</td>
          <td><button class="gm-btn gm-btn-xs" id="gm-forge-unlock-ch${n}">标记完成</button></td>
          <td><button class="gm-btn gm-btn-xs gm-btn-danger" id="gm-forge-reset-ch${n}">重置</button></td>
        </tr>`).join("")}
      </table>
      <div class="gm-quick-actions" style="margin-top:8px">
        <button class="gm-btn gm-btn-sm gm-btn-danger" id="gm-forge-reset-all">🧹 重置所有进度</button>
      </div>

      <div class="gm-section-header" style="margin-top:16px">一键恢复</div>
      <div class="gm-quick-actions">
        <button class="gm-btn gm-btn-sm" id="gm-dev-clear-and-reload" title="清全部 battlecode-* + 刷新">
          🧹 清全部并刷新
        </button>
      </div>
    </div>
  `;

  // ===== 事件绑定 =====

  // 假登录
  container.querySelector('#gm-dev-fake-login')?.addEventListener('click', () => {
    const fakeToken = buildFakeToken(FAKE_USER_OPENID, FAKE_USER_NAME);
    localStorage.setItem("battlecode-auth-token", fakeToken);
    localStorage.setItem("battlecode-current-user", JSON.stringify({ openid: FAKE_USER_OPENID, name: FAKE_USER_NAME }));
    localStorage.setItem("battlecode-user-profile", JSON.stringify({
      openid: FAKE_USER_OPENID,
      name: FAKE_USER_NAME,
      provider: FAKE_USER_PROVIDER,
      avatar: "",
    }));
    // 触发 user-context 重新 hydrate
    import("../core/user-context.js").then((m) => m.notifyUserReady());
    alert(`✅ 已注入假登录态\n\nopenid: ${FAKE_USER_OPENID}\nname: ${FAKE_USER_NAME}\n\n刷新页面后 isLoggedIn() 返回 true。`);
    renderDevTab(container);
  });

  // 登出
  container.querySelector('#gm-dev-logout')?.addEventListener('click', () => {
    localStorage.removeItem("battlecode-auth-token");
    localStorage.removeItem("battlecode-current-user");
    localStorage.removeItem("battlecode-user-profile");
    import("../core/user-context.js").then((m) => m.notifyUserReady());
    renderDevTab(container);
  });

  // 导入生产 token
  container.querySelector('#gm-dev-import-token')?.addEventListener('click', () => {
    const input = document.getElementById('gm-dev-token-input') as HTMLTextAreaElement;
    const token = input?.value.trim();
    if (!token) {
      alert("请先粘贴 JWT token");
      return;
    }
    const claims = decodeJwtClaims(token);
    if (!claims || !claims.openid) {
      alert("❌ Token 格式无效或缺少 openid");
      return;
    }
    if (claims.exp && claims.exp * 1000 <= Date.now()) {
      if (!confirm("⚠️ Token 已过期，仍要注入吗？")) return;
    }
    localStorage.setItem("battlecode-auth-token", token);
    localStorage.setItem("battlecode-current-user", JSON.stringify({
      openid: claims.openid,
      name: claims.name ?? "Imported",
    }));
    localStorage.setItem("battlecode-user-profile", JSON.stringify({
      openid: claims.openid,
      name: claims.name ?? "Imported",
      provider: claims.provider ?? "unknown",
      avatar: claims.avatar ?? "",
    }));
    import("../core/user-context.js").then((m) => m.notifyUserReady());
    const expTxt = claims.exp ? new Date(claims.exp * 1000).toLocaleString() : "—";
    alert(`✅ 已注入生产 token\n\nopenid: ${claims.openid}\nname: ${claims.name ?? "—"}\nprovider: ${claims.provider ?? "—"}\nexp: ${expTxt}\n\n刷新页面后 isLoggedIn() 返回 true。`);
    renderDevTab(container);
  });

  // 只解码
  container.querySelector('#gm-dev-decode-only')?.addEventListener('click', () => {
    const input = document.getElementById('gm-dev-token-input') as HTMLTextAreaElement;
    const preview = document.getElementById('gm-dev-token-preview');
    if (!input?.value.trim()) {
      if (preview) preview.textContent = "(空)";
      return;
    }
    const claims = decodeJwtClaims(input.value.trim());
    if (!claims) {
      if (preview) preview.textContent = "❌ 无法解码";
    } else {
      if (preview) preview.textContent = JSON.stringify(claims, null, 2);
    }
  });

  // 清理 pending
  container.querySelector('#gm-dev-clear-pending-auth')?.addEventListener('click', () => {
    localStorage.removeItem("battlecode-pending-auth-path");
    renderDevTab(container);
  });
  container.querySelector('#gm-dev-clear-pending-forge')?.addEventListener('click', () => {
    localStorage.removeItem("battlecode-pending-forge");
    renderDevTab(container);
  });

  // ===== 铸剑阁进度控制 =====
  import("./forge-progress-service.js").then((fps) => {
    const refreshForge = () => renderDevTab(container);

    container.querySelector('#gm-forge-unlock-all')?.addEventListener('click', () => {
      fps.gmUnlockAllChapters();
      refreshForge();
    });

    [1, 2, 3, 4, 5].forEach((n) => {
      container.querySelector(`#gm-forge-unlock-ch${n}`)?.addEventListener('click', () => {
        fps.gmUnlockChapter(n);
        refreshForge();
      });
      container.querySelector(`#gm-forge-reset-ch${n}`)?.addEventListener('click', () => {
        fps.gmResetChapter(n);
        refreshForge();
      });
    });

    container.querySelector('#gm-forge-reset-all')?.addEventListener('click', () => {
      fps.gmResetForgeProgress();
      refreshForge();
    });
  });

  // 一键清全部 + 刷新
  container.querySelector('#gm-dev-clear-and-reload')?.addEventListener('click', () => {
    if (!confirm('清空所有 battlecode-* 数据并刷新页面？')) return;
    const toRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('battlecode-')) toRemove.push(key);
    }
    toRemove.forEach((k) => localStorage.removeItem(k));
    window.location.reload();
  });
}

// ===== 全局暴露 =====

if (typeof window !== 'undefined') {
  (window as any).__gmTool = { open, close, toggle };
}
